import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import TaskDialog from "@/components/TaskDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  KanbanSquare,
  List as ListIcon,
  Calendar as CalendarIcon,
  Clock,
  Play,
  CheckCircle2,
  AlertOctagon,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Pencil,
  Repeat,
  MessageCircle,
  Paperclip,
} from "lucide-react";

const STATUS_META = {
  pending: { label: "Pendente", bg: "#FDF3E1", border: "#805B10", icon: Clock, dot: "#B87A1A" },
  in_progress: { label: "Em curso", bg: "#E3EDF5", border: "#1E4A75", icon: Play, dot: "#2C74B3" },
  done: { label: "Concluída", bg: "#E1EDE4", border: "#1E5A2C", icon: CheckCircle2, dot: "#2C7A3F" },
};
const OVERDUE = { bg: "#FBE6E4", border: "#8A2A22", dot: "#B12E23", icon: AlertOctagon };

const todayISO = () => new Date().toISOString().slice(0, 10);
const isOverdue = (t) => t.status !== "done" && t.end_date < todayISO();

export default function TasksPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({ total: 0, pending: 0, in_progress: 0, done: 0, overdue: 0 });
  const [commentsCounts, setCommentsCounts] = useState({});
  const [attachmentsCounts, setAttachmentsCounts] = useState({});
  const [teamMembers, setTeamMembers] = useState([]); // resolved names by id
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState("kanban"); // kanban | list | calendar
  const [filterClient, setFilterClient] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadClients = async () => {
    try {
      const r = await api.get("/clients");
      setClients(r.data);
    } catch { /* noop */ }
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterClient !== "all") params.client_id = filterClient;
      if (filterAssignee !== "all") params.assignee_id = filterAssignee;
      if (filterFrom) params.from_date = filterFrom;
      if (filterTo) params.to_date = filterTo;
      const [tR, sR] = await Promise.all([
        api.get("/tasks", { params }),
        api.get("/tasks/summary", { params }),
      ]);
      setTasks(tR.data);
      setSummary(sR.data);
      try {
        const [cR, aR] = await Promise.all([
          api.get("/tasks/comments/counts"),
          api.get("/tasks/attachments/counts"),
        ]);
        setCommentsCounts(cR.data || {});
        setAttachmentsCounts(aR.data || {});
      } catch { setCommentsCounts({}); setAttachmentsCounts({}); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar tarefas");
    } finally {
      setLoading(false);
    }
  };

  // Resolve team members across all visible clients for filter dropdown & names
  const loadTeamMembers = async () => {
    try {
      const targetClients = filterClient === "all" ? clients : clients.filter((c) => c.id === filterClient);
      const seen = new Map();
      for (const c of targetClients) {
        try {
          const r = await api.get("/tasks/team", { params: { client_id: c.id } });
          r.data.forEach((m) => { if (!seen.has(m.id)) seen.set(m.id, m); });
        } catch { /* noop */ }
      }
      setTeamMembers(Array.from(seen.values()));
    } catch { /* noop */ }
  };

  useEffect(() => { loadClients(); }, []);
  useEffect(() => { if (clients.length) loadTeamMembers(); }, [clients, filterClient]);
  useEffect(() => { loadTasks(); }, [filterClient, filterAssignee, filterFrom, filterTo]);

  const clientNameById = useMemo(() => {
    const m = {};
    clients.forEach((c) => (m[c.id] = c.name));
    return m;
  }, [clients]);

  const memberNameById = useMemo(() => {
    const m = {};
    teamMembers.forEach((mem) => (m[mem.id] = mem.name));
    return m;
  }, [teamMembers]);

  const updateTaskStatus = async (task, newStatus) => {
    try {
      await api.put(`/tasks/${task.id}`, { status: newStatus });
      loadTasks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar");
    }
  };

  const deleteTask = async (id, scope = "one") => {
    try {
      const url = scope === "series" ? `/tasks/${id}?scope=series` : `/tasks/${id}`;
      const r = await api.delete(url);
      const n = r.data?.deleted || 1;
      toast.success(n > 1 ? `${n} tarefas eliminadas` : "Tarefa eliminada");
      setConfirmDelete(null);
      loadTasks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao eliminar");
    }
  };

  const openEdit = (t) => { setEditing(t); setDialogOpen(true); };
  const openCreate = () => { setEditing(null); setDialogOpen(true); };

  return (
    <Layout
      subtitle="Painel da equipa"
      title="Tarefas"
      actions={
        <div className="flex gap-2">
          <Button
            onClick={openCreate}
            className="rounded-full h-11 px-5 text-xs uppercase tracking-widest bg-[#3B2B23] text-[#F9F6F0] hover:bg-[#2A1E18]"
            data-testid="new-task-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Nova tarefa
          </Button>
        </div>
      }
    >
      {/* Summary */}
      <section className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4 mb-8" data-testid="tasks-summary">
        <SummaryCard label="Total" value={summary.total} color="#3B2B23" />
        <SummaryCard label="Pendentes" value={summary.pending} color={STATUS_META.pending.dot} />
        <SummaryCard label="Em curso" value={summary.in_progress} color={STATUS_META.in_progress.dot} />
        <SummaryCard label="Concluídas" value={summary.done} color={STATUS_META.done.dot} />
        <SummaryCard label="Atrasadas" value={summary.overdue} color={OVERDUE.dot} />
      </section>

      {/* Filters + view switch */}
      <section className="flex flex-col md:flex-row md:items-end gap-3 md:gap-4 mb-6">
        <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#5C4A43] mb-1">Cliente</div>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-[#EAE3DB] bg-white text-sm"
              data-testid="filter-client"
            >
              <option value="all">Todos</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#5C4A43] mb-1">Responsável</div>
            <select
              value={filterAssignee}
              onChange={(e) => setFilterAssignee(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-[#EAE3DB] bg-white text-sm"
              data-testid="filter-assignee"
              disabled={!isAdmin && teamMembers.length <= 1}
            >
              <option value="all">Todos</option>
              {teamMembers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#5C4A43] mb-1">De</div>
            <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="h-10 bg-white" data-testid="filter-from" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#5C4A43] mb-1">Até</div>
            <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="h-10 bg-white" data-testid="filter-to" />
          </div>
        </div>
        <div className="flex gap-1 bg-white border border-[#EAE3DB] rounded-full p-1 self-start md:self-end">
          <ViewBtn active={view === "kanban"} onClick={() => setView("kanban")} icon={KanbanSquare} label="Kanban" testid="view-kanban" />
          <ViewBtn active={view === "list"} onClick={() => setView("list")} icon={ListIcon} label="Lista" testid="view-list" />
          <ViewBtn active={view === "calendar"} onClick={() => setView("calendar")} icon={CalendarIcon} label="Calendário" testid="view-calendar" />
        </div>
      </section>

      {loading ? (
        <div className="text-center py-16 text-[#5C4A43] text-sm uppercase tracking-widest">A carregar…</div>
      ) : tasks.length === 0 ? (
        <Card className="card-soft p-12 text-center" data-testid="tasks-empty">
          <div className="text-xl text-[#1A1412]">Sem tarefas</div>
          <div className="mt-2 text-[#5C4A43] text-sm">Cria a primeira tarefa da equipa clicando em &quot;Nova tarefa&quot;.</div>
        </Card>
      ) : view === "kanban" ? (
        <KanbanBoard
          tasks={tasks}
          isAdmin={isAdmin}
          user={user}
          clientNameById={clientNameById}
          memberNameById={memberNameById}
          commentsCounts={commentsCounts}
          attachmentsCounts={attachmentsCounts}
          onEdit={openEdit}
          onDelete={(t) => setConfirmDelete(t)}
          onStatusChange={updateTaskStatus}
        />
      ) : view === "list" ? (
        <TaskList
          tasks={tasks}
          isAdmin={isAdmin}
          user={user}
          clientNameById={clientNameById}
          memberNameById={memberNameById}
          commentsCounts={commentsCounts}
          attachmentsCounts={attachmentsCounts}
          onEdit={openEdit}
          onDelete={(t) => setConfirmDelete(t)}
          onStatusChange={updateTaskStatus}
        />
      ) : (
        <TaskCalendar
          tasks={tasks}
          clientNameById={clientNameById}
          onEdit={openEdit}
        />
      )}

      <TaskDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        task={editing}
        clients={clients}
        defaultClientId={filterClient !== "all" ? filterClient : null}
        onSaved={loadTasks}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#F9F6F0]">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar tarefa?</AlertDialogTitle>
            <AlertDialogDescription>
              A tarefa &quot;{confirmDelete?.title}&quot; será removida definitivamente.
              {confirmDelete?.series_id && (
                <span className="block mt-2 text-[#8B5E3C]">
                  Esta tarefa faz parte de uma série recorrente. Podes eliminar apenas esta ocorrência ou toda a série.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            {confirmDelete?.series_id && (
              <AlertDialogAction
                className="bg-[#3B2B23] hover:bg-[#2A1E18] text-white"
                onClick={() => deleteTask(confirmDelete.id, "series")}
                data-testid="confirm-delete-series"
              >
                Eliminar série
              </AlertDialogAction>
            )}
            <AlertDialogAction
              className="bg-[#8A2A22] hover:bg-[#6E2019] text-white"
              onClick={() => deleteTask(confirmDelete.id, "one")}
              data-testid="confirm-delete-task"
            >
              {confirmDelete?.series_id ? "Só esta" : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function ViewBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-full h-9 px-4 text-[10px] uppercase tracking-widest transition-colors ${active ? "bg-[#3B2B23] text-[#F9F6F0]" : "text-[#5C4A43] hover:bg-[#EAE3DB]"}`}
      data-testid={testid}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <Card className="card-soft p-4 sm:p-5" data-testid={`summary-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-[#5C4A43]">
        <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em]">{label}</span>
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
      </div>
      <div className="mt-2 text-3xl text-[#1A1412] font-bold">{value}</div>
    </Card>
  );
}

function KanbanBoard({ tasks, isAdmin, user, clientNameById, memberNameById, commentsCounts, attachmentsCounts, onEdit, onDelete, onStatusChange }) {
  const cols = [
    { key: "pending", ...STATUS_META.pending },
    { key: "in_progress", ...STATUS_META.in_progress },
    { key: "done", ...STATUS_META.done },
  ];
  const onDragStart = (e, taskId) => e.dataTransfer.setData("text/plain", taskId);
  const onDropCol = (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    const t = tasks.find((x) => x.id === id);
    if (t && t.status !== status) onStatusChange(t, status);
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {cols.map((col) => {
        const items = tasks.filter((t) => t.status === col.key);
        return (
          <div
            key={col.key}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDropCol(e, col.key)}
            className="rounded-2xl p-3 min-h-[280px]"
            style={{ background: col.bg }}
            data-testid={`kanban-col-${col.key}`}
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: col.dot }} />
                <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: col.border }}>
                  {col.label}
                </span>
              </div>
              <span className="text-[10px] text-[#5C4A43]">{items.length}</span>
            </div>
            <div className="space-y-2">
              {items.map((t) => (
                <TaskCard
                  key={t.id}
                  task={t}
                  isAdmin={isAdmin}
                  user={user}
                  clientName={clientNameById[t.client_id]}
                  assigneeNames={(t.assignee_ids && t.assignee_ids.length ? t.assignee_ids : (t.assignee_id ? [t.assignee_id] : [])).map((id) => memberNameById[id]).filter(Boolean)}
                  commentsCount={commentsCounts[t.id] || 0}
                  attachmentsCount={attachmentsCounts[t.id] || 0}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onDragStart={onDragStart}
                  draggable
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TaskCard({ task, isAdmin, user, clientName, assigneeNames, commentsCount, attachmentsCount, onEdit, onDelete, onDragStart, draggable }) {
  const overdue = isOverdue(task);
  const meta = STATUS_META[task.status] || STATUS_META.pending;
  const currentIds = task.assignee_ids && task.assignee_ids.length ? task.assignee_ids : (task.assignee_id ? [task.assignee_id] : []);
  const canEdit = isAdmin || currentIds.includes(user?.id) || task.created_by === user?.id;
  const canDelete = isAdmin || task.created_by === user?.id;
  const displayAssignees = (assigneeNames || []).length > 2
    ? `${assigneeNames[0]} +${assigneeNames.length - 1}`
    : (assigneeNames || []).join(", ");
  return (
    <div
      draggable={draggable}
      onDragStart={(e) => onDragStart?.(e, task.id)}
      className="bg-white rounded-xl p-3 border-l-4 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing"
      style={{ borderLeftColor: overdue ? OVERDUE.border : meta.border }}
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-[#1A1412] leading-snug line-clamp-2 flex items-center gap-1.5">
            {task.series_id && (
              <Repeat className="w-3 h-3 text-[#8B5E3C] flex-shrink-0" data-testid={`recurring-badge-${task.id}`} aria-label="Tarefa recorrente" />
            )}
            <span className="min-w-0">{task.title}</span>
          </div>
          {clientName && (
            <div className="text-[9px] uppercase tracking-widest text-[#8B5E3C] mt-1">{clientName}</div>
          )}
        </div>
        {overdue && (
          <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: OVERDUE.bg, color: OVERDUE.border }} data-testid={`overdue-badge-${task.id}`}>
            <AlertOctagon className="w-3 h-3" /> Atrasada
          </span>
        )}
      </div>
      {task.description && (
        <div className="text-xs text-[#5C4A43] mt-2 line-clamp-2 leading-relaxed">{task.description}</div>
      )}
      <div className="mt-3 flex items-center justify-between text-[10px] text-[#5C4A43]">
        <span className="flex items-center gap-1">
          <CalendarIcon className="w-3 h-3" />
          {task.start_date === task.end_date ? task.start_date : `${task.start_date} → ${task.end_date}`}
        </span>
        <span className="flex items-center gap-2 max-w-[45%]">
          {commentsCount > 0 && (
            <span className="flex items-center gap-0.5 text-[#8B5E3C]" title={`${commentsCount} comentário(s)`} data-testid={`comment-count-${task.id}`}>
              <MessageCircle className="w-3 h-3" />{commentsCount}
            </span>
          )}
          {attachmentsCount > 0 && (
            <span className="flex items-center gap-0.5 text-[#8B5E3C]" title={`${attachmentsCount} anexo(s)`} data-testid={`attachment-count-${task.id}`}>
              <Paperclip className="w-3 h-3" />{attachmentsCount}
            </span>
          )}
          {displayAssignees && (
            <span className="truncate" title={(assigneeNames || []).join(", ")}>{displayAssignees}</span>
          )}
        </span>
      </div>
      {(canEdit || canDelete) && (
        <div className="mt-2 pt-2 border-t border-[#EAE3DB] flex items-center justify-end gap-1">
          {canEdit && (
            <button
              onClick={() => onEdit(task)}
              className="p-1.5 rounded-full hover:bg-[#EAE3DB] text-[#3B2B23]"
              data-testid={`edit-task-${task.id}`}
              aria-label="Editar tarefa"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => onDelete(task)}
              className="p-1.5 rounded-full hover:bg-[#FBE6E4] text-[#8A2A22]"
              data-testid={`delete-task-${task.id}`}
              aria-label="Eliminar tarefa"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function TaskList({ tasks, isAdmin, user, clientNameById, memberNameById, commentsCounts, attachmentsCounts, onEdit, onDelete, onStatusChange }) {
  return (
    <Card className="card-soft overflow-hidden">
      <div className="divide-y divide-[#EAE3DB]">
        {tasks.map((t) => {
          const overdue = isOverdue(t);
          const meta = STATUS_META[t.status] || STATUS_META.pending;
          const currentIds = t.assignee_ids && t.assignee_ids.length ? t.assignee_ids : (t.assignee_id ? [t.assignee_id] : []);
          const canEdit = isAdmin || currentIds.includes(user?.id) || t.created_by === user?.id;
          const canDelete = isAdmin || t.created_by === user?.id;
          const assigneeNames = currentIds.map((id) => memberNameById[id]).filter(Boolean);
          return (
            <div key={t.id} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid={`task-list-item-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: overdue ? OVERDUE.dot : meta.dot }} />
                  <span className="text-sm font-bold text-[#1A1412] flex items-center gap-1.5">
                    {t.series_id && <Repeat className="w-3 h-3 text-[#8B5E3C]" aria-label="Tarefa recorrente" />}
                    {t.title}
                  </span>
                  {overdue && (
                    <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: OVERDUE.bg, color: OVERDUE.border }}>
                      Atrasada
                    </span>
                  )}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-[#5C4A43] flex flex-wrap gap-x-3 gap-y-1">
                  <span>{clientNameById[t.client_id]}</span>
                  <span>{t.start_date} → {t.end_date}</span>
                  {assigneeNames.length > 0 && <span>· {assigneeNames.join(", ")}</span>}
                  {(commentsCounts[t.id] || 0) > 0 && (
                    <span className="flex items-center gap-0.5 text-[#8B5E3C]"><MessageCircle className="w-3 h-3" />{commentsCounts[t.id]}</span>
                  )}
                  {(attachmentsCounts[t.id] || 0) > 0 && (
                    <span className="flex items-center gap-0.5 text-[#8B5E3C]"><Paperclip className="w-3 h-3" />{attachmentsCounts[t.id]}</span>
                  )}
                </div>
                {t.description && <div className="mt-2 text-xs text-[#5C4A43] leading-relaxed line-clamp-2">{t.description}</div>}
              </div>
              <div className="flex items-center gap-2 self-start sm:self-center flex-wrap">
                <select
                  value={t.status}
                  onChange={(e) => onStatusChange(t, e.target.value)}
                  disabled={!canEdit}
                  className="h-9 px-3 rounded-full border text-[10px] uppercase tracking-widest bg-white font-bold"
                  style={{ borderColor: meta.border, color: meta.border }}
                  data-testid={`status-select-${t.id}`}
                >
                  <option value="pending">Pendente</option>
                  <option value="in_progress">Em curso</option>
                  <option value="done">Concluída</option>
                </select>
                {canEdit && (
                  <button onClick={() => onEdit(t)} className="p-2 rounded-full hover:bg-[#EAE3DB] text-[#3B2B23]" data-testid={`list-edit-${t.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
                {canDelete && (
                  <button onClick={() => onDelete(t)} className="p-2 rounded-full hover:bg-[#FBE6E4] text-[#8A2A22]" data-testid={`list-delete-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function TaskCalendar({ tasks, clientNameById, onEdit }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() + 1 };
  });
  const firstDay = new Date(cursor.y, cursor.m - 1, 1);
  const daysInMonth = new Date(cursor.y, cursor.m, 0).getDate();
  const startOffset = (firstDay.getDay() + 6) % 7; // Mon=0
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const iso = (d) => `${cursor.y}-${String(cursor.m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const tasksOnDay = (d) => {
    const day = iso(d);
    return tasks.filter((t) => t.start_date <= day && t.end_date >= day);
  };

  const prevMonth = () => setCursor((c) => c.m === 1 ? { y: c.y - 1, m: 12 } : { y: c.y, m: c.m - 1 });
  const nextMonth = () => setCursor((c) => c.m === 12 ? { y: c.y + 1, m: 1 } : { y: c.y, m: c.m + 1 });
  const monthName = new Date(cursor.y, cursor.m - 1, 1).toLocaleDateString("pt-PT", { month: "long", year: "numeric" });

  return (
    <Card className="card-soft p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-2 rounded-full hover:bg-[#EAE3DB] text-[#3B2B23]" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
        <div className="text-lg text-[#1A1412] capitalize font-bold">{monthName}</div>
        <button onClick={nextMonth} className="p-2 rounded-full hover:bg-[#EAE3DB] text-[#3B2B23]" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
        {["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"].map((d) => (
          <div key={d} className="text-[9px] uppercase tracking-widest text-[#5C4A43] text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {cells.map((d, i) => (
          <div key={i} className={`min-h-[70px] sm:min-h-[90px] rounded-lg p-1.5 ${d ? "bg-white border border-[#EAE3DB]" : ""}`}>
            {d && (
              <>
                <div className="text-[10px] text-[#5C4A43] mb-1">{String(d).padStart(2, "0")}</div>
                <div className="space-y-1">
                  {tasksOnDay(d).slice(0, 3).map((t) => {
                    const overdue = isOverdue(t);
                    const meta = STATUS_META[t.status] || STATUS_META.pending;
                    return (
                      <button
                        key={t.id}
                        onClick={() => onEdit(t)}
                        className="w-full text-left px-1.5 py-1 rounded text-[9px] leading-tight truncate font-bold hover:opacity-80"
                        style={{ background: overdue ? OVERDUE.bg : meta.bg, color: overdue ? OVERDUE.border : meta.border, borderLeft: `3px solid ${overdue ? OVERDUE.border : meta.border}` }}
                        title={`${t.title} · ${clientNameById[t.client_id] || ""}`}
                        data-testid={`cal-task-${t.id}`}
                      >
                        {t.title}
                      </button>
                    );
                  })}
                  {tasksOnDay(d).length > 3 && (
                    <div className="text-[9px] text-[#8B5E3C] px-1">+ {tasksOnDay(d).length - 3}</div>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
